import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Admin-Analyse der Zeiterfassung: tenant-weite Auswertung über ALLE Mitarbeiter
 * (das Self-Service-Dashboard zeigt nur die eigenen Stunden). Serverseitig
 * aggregiert — Recht `timetracking.manage`. Stunden als Dezimalzahl, Kosten als
 * Money (bigint-Mikro, string-serialisiert) — §6, keine Float-Summen für Geld.
 */

/** ISO-Datum (JJJJ-MM-TT) für den Zeitraum-Filter. */
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet JJJJ-MM-TT');

/** Filter der Zeit-Analyse. Ohne Angabe: der laufende Kalendermonat (Europe/Berlin). */
export const timeTrackingAnalyticsQuerySchema = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /** Auf eine Gesellschaft (LegalEntity) eingrenzen (sonst alle). */
  legalEntityId: z.string().uuid().optional(),
  /** Auf einen Mitarbeiter eingrenzen (sonst alle). */
  employeeId: z.string().uuid().optional(),
});
export type TimeTrackingAnalyticsQuery = z.infer<typeof timeTrackingAnalyticsQuerySchema>;

/** Auswahloption für den Mitarbeiter-Filter (distinct aus den Zeitbuchungen). */
export const timeTrackingAnalyticsEmployeeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type TimeTrackingAnalyticsEmployee = z.infer<typeof timeTrackingAnalyticsEmployeeSchema>;

/** Eine Aufschlüsselungs-Zeile (Mitarbeiter / Projekt / Kategorie / Gesellschaft). */
export const ttAnalyticsBreakdownSchema = z.object({
  /** Anzeige-Name (z. B. Mitarbeiter-, Projekt-, Kategorie- oder Gesellschaftsname). */
  label: z.string(),
  /** Zweitzeile (z. B. Gesellschaft beim Projekt) — optional. */
  subLabel: z.string().nullable(),
  hours: z.number(),
  cost: moneySchema,
});
export type TtAnalyticsBreakdown = z.infer<typeof ttAnalyticsBreakdownSchema>;

/** Ein Tagespunkt des Stunden-Verlaufs (Berliner Kalendertag). */
export const ttAnalyticsDayPointSchema = z.object({
  /** Tag als `JJJJ-MM-TT` (chronologisch sortierbar). */
  date: z.string(),
  hours: z.number(),
});
export type TtAnalyticsDayPoint = z.infer<typeof ttAnalyticsDayPointSchema>;

/** Ergebnis der Zeit-Analyse für einen Zeitraum. */
export const timeTrackingAnalyticsSchema = z.object({
  /** Aufgelöster Zeitraum (Berliner Kalendertage, `to` inklusive). */
  from: z.string(),
  to: z.string(),
  totalHours: z.number(),
  totalCost: moneySchema,
  /** Anzahl Mitarbeiter mit Buchungen im Zeitraum. */
  employeeCount: z.number().int(),
  /** Anzahl gezählter Buchungen. */
  entryCount: z.number().int(),
  byEmployee: z.array(ttAnalyticsBreakdownSchema),
  byProject: z.array(ttAnalyticsBreakdownSchema),
  byCategory: z.array(ttAnalyticsBreakdownSchema),
  byLegalEntity: z.array(ttAnalyticsBreakdownSchema),
  byDay: z.array(ttAnalyticsDayPointSchema),
});
export type TimeTrackingAnalytics = z.infer<typeof timeTrackingAnalyticsSchema>;

/**
 * Einzelbuchungs-Tabelle der Admin-Analyse (Recht `timetracking.manage`).
 *
 * Die Aufschlüsselungen oben sind Summen — hier steht jede Buchung einzeln, damit
 * eine auffällige Zahl im Diagramm nachvollziehbar wird („welche Buchungen stecken
 * dahinter?"). Nur lesend; korrigiert wird dort, wo Buchungen entstehen.
 *
 * Der Zeitraum-/Gesellschafts-/Mitarbeiter-Filter ist derselbe wie für die
 * Diagramme (`timeTrackingAnalyticsQuerySchema`); dazu kommen Projekt, Kategorie,
 * Unterkategorie, Freitextsuche und Seitenzahl. Laufende Timer bleiben — wie in
 * der Aggregation — außen vor, sonst wichen Tabelle und Diagramm voneinander ab.
 */
export const ttAllEntriesSortSchema = z.enum([
  'date',
  '-date',
  'hours',
  '-hours',
  'employee',
  '-employee',
]);
export type TtAllEntriesSort = z.infer<typeof ttAllEntriesSortSchema>;

export const ttAllEntriesQuerySchema = timeTrackingAnalyticsQuerySchema.extend({
  /** Zeiterfassungs-Projekt (`TtProject`), nicht das ProERP-Projekt. */
  ttProjectId: z.string().uuid().optional(),
  ttCategoryId: z.string().uuid().optional(),
  ttSubCategoryId: z.string().uuid().optional(),
  /** Freitext über Mitarbeiter, Projektname/-nummer, Kategorie und Notiz. */
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().positive().default(1),
  /**
   * Max. 500 — deckt sich mit `DataTable`s Standard-Seitengrößen (100/250/500,
   * `apps/web/src/components/DataTable.tsx`). Vorher lag die Grenze bei 200:
   * die Tabelle „Alle Buchungen" bot 250/500 an, der Server lehnte beides mit
   * 422 ab, und die Seite zeigte das fälschlich als „keine Buchungen" statt
   * eines Fehlers (Bug, 2026-08-06 gemeldet) — s. Moduldoku.
   */
  pageSize: z.coerce.number().int().positive().max(500).default(50),
  sort: ttAllEntriesSortSchema.default('-date'),
});
export type TtAllEntriesQuery = z.infer<typeof ttAllEntriesQuerySchema>;

export const ttAllEntryRowSchema = z.object({
  id: z.string().uuid(),
  /** Berliner Kalendertag der Buchung (JJJJ-MM-TT) — wie in der Aggregation. */
  day: z.string(),
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  hours: z.number(),
  legalEntityName: z.string().nullable(),
  /**
   * Anzeigename des Projekts: Zeiterfassungs-Projekt, sonst ProERP-Projekt, sonst
   * null. Dieselbe Regel wie `projectLabel` in der Aggregation — zwei Regeln wären
   * zwei Wahrheiten.
   */
  projectLabel: z.string().nullable(),
  /** ProERP-Projekt (für den Sprung ins Projekt); null bei reiner TT-Buchung. */
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  ttCategoryName: z.string().nullable(),
  ttSubCategoryName: z.string().nullable(),
  /** Kosten der Buchung (Stunden × Satz); null, wenn kein Satz hinterlegt ist. */
  cost: moneySchema.nullable(),
  note: z.string().nullable(),
});
export type TtAllEntryRow = z.infer<typeof ttAllEntryRowSchema>;

/**
 * Eine Seite der Einzelbuchungen. `totalHours`/`totalCost` sind die Summen über
 * ALLE Treffer des Filters, nicht nur über die angezeigte Seite — die Frage
 * „wie viele Stunden sind das zusammen?" soll man nicht seitenweise addieren müssen.
 */
export const ttAllEntriesPageSchema = z.object({
  items: z.array(ttAllEntryRowSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  totalHours: z.number(),
  totalCost: moneySchema,
});
export type TtAllEntriesPage = z.infer<typeof ttAllEntriesPageSchema>;
